import { useTranslation } from 'react-i18next';
import { ReactElement, useState, useCallback } from 'react';
import { DrawerPanel } from '@dmc/ui';
import { CasFormFilePicker } from '@cas/components/inputs/cas-form-file-picker/CasFormFilePicker';
import { QueryTag, TagList } from '@casops/store/api';
import { TagDescription } from '@reduxjs/toolkit/query';
import { FileUploadModel } from '@casops/store/apis/commonModels';
import useFileUploadForm from '@casops/components/layout/model-form/useFileUploadForm';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import { downloadBlob } from '@casops/shared/utils/fileDownloads';
import { VdsButton, VdsIcon, VdsNotification } from '@volt/volt-extended-components-react';
import { PreApprovalImportResponse } from '@casops/store/apis/assetApprovalsApi';
import styles from './PreApprovalImportPanel.module.scss';

type FormModel = FileUploadModel;

const endpointUrl = 'api/assetapprovals';
const tagsToClear: TagDescription<QueryTag>[] = [{ type: QueryTag.AssetApproval, id: TagList }];

const initialValues: FormModel = {
  file: undefined,
};

const handleTemplateDownload = (): void => {
  const approvalTemplate = 'Asset Name,Configuration Group,Asset Groups\r\n';
  downloadBlob(approvalTemplate, 'cas-preapprovals.csv', 'text/csv;charset=utf-8;');
};

const isPreApprovalImportResponse = (data: unknown): data is PreApprovalImportResponse => {
  return typeof data === 'object' && data !== null && 'importFailures' in data;
};

export function PreApprovalImportPanel(): ReactElement {
  const [informativeMessage, setInformativeMessage] = useState<string>('');
  const { t } = useTranslation();

  const SuccessWithFailuresResponseMessage = useCallback(
    (resp: PreApprovalImportResponse): boolean => {
      const failures = resp.importFailures;
      if (failures.length > 0) {
        const lineNumbers = failures.map(({ lineNumber }) => String(lineNumber)).join(', ');
        setInformativeMessage(
          t('SomePreApprovalsImportSuccess', {
            lineNumbers,
          }),
        );
        return false;
      }
      return true;
    },
    [t],
  );

  const SuccessWithFailuresResetNotification = useCallback(() => {
    setInformativeMessage('');
  }, []);

  const formProps = useFileUploadForm<FileUploadModel, PreApprovalImportResponse>({
    endpointUrl,
    SuccessWithFailuresResponseGuard: isPreApprovalImportResponse,
    SuccessWithFailuresResponseMessage: SuccessWithFailuresResponseMessage,
    SuccessWithFailuresResetNotification: SuccessWithFailuresResetNotification,
    tagsToClear,
    defaultValues: initialValues,
    successMessage: t('PreApprovalsImportSuccess'),
  });

  return (
    <DrawerPanel header={t('ImportPreApprovals')}>
      <ModelForm {...formProps} submitLabel={t('Import')}>
        <>
          {informativeMessage && (
            <VdsNotification variant="inline" status="informative" showCloseButton={false}>
              <span slot="header" style={{ paddingRight: 'var(--spacing-03)' }}>
                {t('ImportDetails')}
              </span>
              <span slot="body">{informativeMessage}</span>
            </VdsNotification>
          )}
          <CasFormFilePicker
            control={formProps.control}
            name="file"
            accept=".csv"
            minHeight="200px"
          />
          <div className={styles.link}>
            <VdsButton variant="ghost" onClick={handleTemplateDownload}>
              {t('DownloadCsvTemplate')}
              <VdsIcon slot="icon-left" svgIcon="download" />
            </VdsButton>
          </div>
        </>
      </ModelForm>
    </DrawerPanel>
  );
}

export default PreApprovalImportPanel;
